// Floating logo import
import logo from './assets/logo.png';
import React, { useEffect, useRef, useState } from 'react';
import RegisterBusinessForm from "./RegisterBusinessForm";
import CreateZoneForm from "./CreateZoneForm";
import { UserBusinesses } from "./UserBusinesses";
import { Business } from "./Business";
import { UserZones } from "./UserZones";
import { AroundMe } from "./AroundMe";
import { ZoneDetails } from "./ZoneDetails";
import HomePageView from "./views/HomePageView";
import FavoritesView from "./views/FavoritesView";
import { StoreView } from "./StoreView";
import { StoreAdmin } from "./StoreAdmin";
import { StoreInviteAccept } from "./StoreInviteAccept";
import { JoinZone } from "./JoinZone";
import { AdminDashboard } from "./AdminDashboard";
import { SharedFavoritesList } from "./SharedFavoritesList";
import { NewOrdersNotifier } from "./components/NewOrdersNotifier";
// Legacy icons now handled inside Navbar component
import userIcon from './assets/userIcon.png'; // kept for potential future removal
import searchIcon from './assets/search.png';
import notificationsIcon from './assets/notifications.png';
import { Contact } from './Contact';
import { Store, Calendar, Handshake, LogIn } from "lucide-react";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { LoginPrompt } from "./Login";
import icons, { preloadIcons } from "./IconLoader";
import plusIcon from './assets/new.png';
import CreateCommunityPost from "./CreateCommunityPost";
import { Breadcrumbs } from "./Breadcrumbs"; // still used inside new Navbar
import telegramIcon from './assets/telegram.png';
import { motion } from 'framer-motion';
import Navbar from './components/layout/Navbar';
import FooterNav from './components/layout/FooterNav';
import About from "./about";
import IntroHelpOverlay from "./IntroHelpOverlay";
import AIChatBar from "./components/AIChatBar";
import WhatsHappeningTodayPopup from "./components/WhatsHappeningTodayPopup";
import { fetchWhatsHappeningInUserZones, shouldShowWhatsHappeningPopup, markWhatsHappeningPopupShown } from "./utils/whatsHappeningToday";
import { cacheSafeFetch } from "./cacheSafeFetch";
import GroupPage from './GroupPage';
import { ScrollToTop } from './components/ScrollToTop';
import { Modal, BottomSheet, ShareMenu, Button, Card } from './components/ui';

export function TelegramLoginButton() {
    // Generate a unique id for the widget container
    const [containerId] = useState(() => `telegram-login-widget-container-${Math.random().toString(36).substr(2, 9)}`);

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://telegram.org/js/telegram-widget.js?22";
        script.async = true;
        script.setAttribute("data-telegram-login", "MekomitBot");
        script.setAttribute("data-size", "large");
        script.setAttribute("data-userpic", "false");
        script.setAttribute("data-auth-url", "https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/telegram-login");
        script.setAttribute("data-request-access", "write");

        const container = document.getElementById(containerId);
        if (container) {
            container.innerHTML = "";
            container.appendChild(script);
        }
    }, [containerId]);

    return <div id={containerId} />;
}

export const handleGoogleLogin = async (accessToken) => {
    try {
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });

        if (!res.ok) throw new Error('Failed to fetch user info');

        const data = await res.json();
        console.log("👤 Full Google User info:", data);
        console.log("📸 Profile picture URL:", data.picture);
        console.log("👤 User name:", data.name);
        console.log("📧 Email:", data.email);

        // Use Google's 'sub' (subject) as the unique user ID
        // This is Google's permanent unique identifier for the user
        const chatId = data.sub;
        console.log("🆔 Using Google sub as chat_id:", chatId);

        // TODO: Backend /save-google-user endpoint has a bug (500 error)
        // For now, we'll just use the Google sub directly as the chat_id
        // The backend should be fixed to properly handle user registration

        if (!chatId || chatId === 'undefined') {
            console.error("❌ Invalid chat_id received:", chatId);
            alert("שגיאה: לא התקבל מזהה משתמש תקין");
            return;
        }

        // Save user data to localStorage
        localStorage.setItem('chat_id', chatId);
        localStorage.setItem('user_name', data.name || 'משתמש');
        localStorage.setItem('user_photo', data.picture || '');
        localStorage.setItem('email', data.email || '');

        console.log("✅ Saved user data to localStorage:", {
            chat_id: chatId,
            user_name: data.name,
            user_photo: data.picture,
            email: data.email
        });

        // If a gated action stored a return target (e.g. the customers-club
        // login from a store), go back there instead of home. Uses a dedicated
        // key — `redirectAfterLogin` is overwritten elsewhere on the '/' load.
        const redirect = sessionStorage.getItem('loginReturnUrl');
        sessionStorage.removeItem('loginReturnUrl');
        if (redirect && redirect.startsWith('/') && redirect !== '/') {
            window.location.href = redirect;
        } else {
            navigate('home');
            window.location.reload();
        }
    } catch (err) {
        console.error("Google login error:", err);
        alert("שגיאה בהתחברות עם Google");
    }
};

export const navigate = (firstArg, secondArg = '', breadcrumbLabel = '') => {
    let url, view;

    console.log('🔍 navigate called with:', { firstArg, secondArg, breadcrumbLabel });

    // Special-case: home should have a clean root URL without query params
    if (firstArg === 'home') {
        url = '/';
        view = 'home';
    } else if (firstArg === 'zones') {
        url = '/?view=zones';
        view = 'zones';
    } else if (firstArg.startsWith('http')) {
        url = firstArg;
    } else if (firstArg.startsWith('?')) {
        // For query string navigation, always use root path to avoid accumulating paths
        url = `/${firstArg}`;
        // Check if entity param is present to auto-set business view
        const params = new URLSearchParams(firstArg);
        if (params.has('entity')) {
            view = 'business';
        } else if (params.has('group_id') || params.has('group')) {
            view = 'group';
        }
        console.log('🔍 Query string navigation:', { url, view, entity: params.get('entity') });
    } else if (firstArg === 'store' || firstArg === 'store-admin') {
        // Handle store and store-admin navigation
        // secondArg should be like "slug=test"
        const params = new URLSearchParams(secondArg);
        const slug = params.get('slug');
        if (slug) {
            // For store-admin, use query params
            if (firstArg === 'store-admin') {
                params.set('view', 'store-admin');
                url = `/${slug}?${params.toString()}`;
                view = 'store-admin';
            } else {
                // For store, use clean URL
                url = `/${slug}`;
                view = 'store';
            }
        } else {
            // Fallback to query params if no slug provided
            params.set('view', firstArg);
            url = `${window.location.pathname}?${params.toString()}`;
            view = firstArg;
        }
    } else {
        const params = new URLSearchParams(secondArg);
        params.set('view', firstArg);
        url = `${window.location.pathname}?${params.toString()}`;
        view = firstArg;
    }

    console.log('🔍 Pushing state:', { url, view });
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));

    if (view && typeof window.setView === 'function') {
        console.log('🔍 Calling window.setView with:', view);
        window.setView(view);
    } else {
        console.log('🔍 Not calling window.setView:', { view, hasSetView: typeof window.setView === 'function' });
    }

    if (typeof window.addBreadcrumb === 'function' && breadcrumbLabel) {
        window.addBreadcrumb({ view, params: secondArg, label: breadcrumbLabel });
    }
};

export async function safeFetch(url, chatId = undefined, options = {}) {
    const headers = {
        ...options.headers
    };

    try {
        const urlObj = new URL(url, window.location.origin);
        if (!urlObj.searchParams.has('chat_id') && chatId) {
            urlObj.searchParams.append('chat_id', chatId);
        }

        const fetchOptions = {
            method: options.method || 'GET', // ✅ DEFAULT TO GET IF NO METHOD
            ...options,
            headers,
        };

        const res = await fetch(urlObj.toString(), fetchOptions);

        if (res.status === 401) {
            console.warn('❌ Session invalid or expired. Redirecting to login...');
            localStorage.removeItem('session_id');
            document.cookie = "mekomitChatId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
            // Clean root URL instead of query params
            window.location.href = '/';
            throw new Error('Unauthorized');
        }

        return res;
    } catch (err) {
        console.error('❌ Network or unexpected fetch error:', err);
        throw err;
    }
}

/**
 * GuestAuthPrompt — when a guest user reaches a logged-in-only page
 * (store-invite acceptance, private zone join), we show this card.
 * One reusable component instead of duplicating the same layout twice.
 */
function GuestAuthPrompt({ title, description, primaryLabel, onPrimary, secondaryLabel, onSecondary, footnote }) {
    return (
        <div dir="rtl" className="min-h-screen flex items-center justify-center px-4 py-12 bg-surface-base">
            <Card variant="elevated" padding="lg" className="max-w-md w-full text-center space-y-6">
                <div className="mx-auto w-14 h-14 rounded-pill bg-brand-50 text-brand-700 inline-flex items-center justify-center">
                    <LogIn className="w-7 h-7" strokeWidth={1.75} />
                </div>
                <div className="space-y-2">
                    <h2 className="heading-lg">{title}</h2>
                    <p className="text-ink-muted leading-relaxed">{description}</p>
                </div>
                <div className="flex flex-col gap-2.5 pt-2">
                    <Button variant="primary" size="lg" fullWidth onClick={onPrimary}>{primaryLabel}</Button>
                    <Button variant="ghost" size="md" fullWidth onClick={onSecondary}>{secondaryLabel}</Button>
                </div>
                {footnote && <p className="text-xs text-ink-subtle">{footnote}</p>}
            </Card>
        </div>
    );
}

window.forceLogout = () => {
    document.cookie = "mekomitChatId=; path=/; max-age=0";
    sessionStorage.removeItem('redirectAfterLogin');
    window.setChatId && window.setChatId(null);
    window.setView && window.setView('zones');
    // Replace state with clean root URL (no view/chat_id params)
    window.history.replaceState({}, '', '/');
};

export default function TelegramMiniApp() {
    const [view, setView] = useState('home');
    const telegramRef = useRef(null);
    const [chatId, setChatId] = useState(() => {
        // 1. Try sessionStorage
        const stored = localStorage.getItem('chat_id');
        if (stored && stored !== 'null') return stored;

        // 2. Try cookie fallback (optional, but slightly less secure)
        const cookie = document.cookie
            .split('; ')
            .find(row => row.startsWith('mekomitChatId='));
        const cookieValue = cookie?.split('=')[1];
        if (cookieValue) return cookieValue;

        // 3. Fallback: null, user needs to log in
        sessionStorage.setItem('redirectAfterLogin', window.location.search);
        return null;
    });

    const isGuest = chatId === 'guest';
    const [isShimmering, setIsShimmering] = useState(false);
    const [isSpinning, setIsSpinning] = useState(true);

    // Check if this is a shared link (entity in URL or store slug in path or favorites list)
    const urlParams = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;
    const hasEntity = urlParams.has('entity');
    const hasStoreSlug = pathname && pathname !== '/' && !pathname.startsWith('/store-invite');
    const hasFavoritesList = urlParams.get('view') === 'favorites-list' && urlParams.has('list_id');
    const isSharedLink = hasEntity || hasStoreSlug || hasFavoritesList;

    if (!chatId && !isSharedLink) {
        sessionStorage.setItem('redirectAfterLogin', window.location.search);
    }
    const [showShareOptions, setShowShareOptions] = useState(false);
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [userBusinesses, setUserBusinesses] = useState([]);
    const [userNotifications, setUserNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [bgImage, setBgImage] = useState('');
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [authChecked, setAuthChecked] = useState(false);
    const [showCreateOptions, setShowCreateOptions] = useState(false);
    const [showPostOptions, setShowPostOptions] = useState(false);
    const [zoneDetailsActiveTab, setZoneDetailsActiveTab] = useState('businesses');
    const [storeSlug, setStoreSlug] = useState(null);

    // What's Happening Today popup state
    const [showWhatsHappeningPopup, setShowWhatsHappeningPopup] = useState(false);
    const [whatsHappeningData, setWhatsHappeningData] = useState({ openBusinesses: [], todayEvents: [] });

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const chatId = urlParams.get("id");

        // Handle Telegram login via id param
        if (chatId) {
            localStorage.setItem('chat_id', chatId);
            // Return to a stored target (e.g. customers-club login from a store).
            const redirect = sessionStorage.getItem('loginReturnUrl');
            sessionStorage.removeItem('loginReturnUrl');
            if (redirect && redirect.startsWith('/') && redirect !== '/') {
                window.location.href = redirect;
            } else {
                navigate('home');
                window.location.reload();
            }
            return;
        }

        // Handle entity parameter - automatically show business view
        const entityId = urlParams.get("entity");
        if (entityId && !urlParams.get("view")) {
            // Auto-set view to business if entity is present but view is not
            setView('business');
        }
    }, []);

    useEffect(() => {
        if (!authChecked) return;  // 🧠 Just exit early inside
        // fetch user notifications here
    }, [chatId, authChecked]);

    useEffect(() => {
        const storedChatId = localStorage.getItem('chat_id');

        if (!storedChatId || storedChatId === 'null' || storedChatId.length < 5) {
            console.warn('⛔ Invalid or missing chat_id.');
            setChatId(null);
            setAuthChecked(true);
            sessionStorage.setItem('redirectAfterLogin', window.location.search); // this can still be useful
        } else {
            setChatId(storedChatId);
            setAuthChecked(true);
        }
    }, []);

    // Initialize push notifications when user logs in
    useEffect(() => {
        if (chatId && chatId !== 'guest' && authChecked) {
            import('./utils/pushNotifications').then(({ initializePushNotifications }) => {
                initializePushNotifications(chatId)
                    .then(subscribed => {
                        if (subscribed) {
                            console.log('Push notifications initialized');
                        }
                    })
                    .catch(error => {
                        console.error('Failed to initialize push notifications:', error);
                    });
            }).catch(error => {
                console.error('Failed to load push notifications module:', error);
            });
        }
    }, [chatId, authChecked]);

    useEffect(() => {
        window.addBreadcrumb = (crumb) => {
            setBreadcrumbs((prev) => [...prev, crumb]);
        };
        return () => {
            delete window.addBreadcrumb;
        };
    }, []);

    useEffect(() => {
        const setViewportHeight = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };

        setViewportHeight();
        window.addEventListener('resize', setViewportHeight);

        return () => {
            window.removeEventListener('resize', setViewportHeight);
        };
    }, []);

    useEffect(() => {
        const isMobile = window.innerWidth < 768;
        // Previously set to undefined which produced CSS url(undefined) => network request /undefined.
        // Use empty string to suppress background image without triggering a fetch.
        if (isMobile) {
            setBgImage('');
        } else {
            // (Optional) could set a desktop background here in future
            setBgImage('');
        }
    }, []);

    // Debug: warn when no background image to make root-cause obvious in production logs
    useEffect(() => {
        if (!bgImage) {
            console.warn('[App] No bgImage set; backgroundImage style omitted (prevents request to /undefined).');
        }
    }, [bgImage]);


    const handleAuthRedirect = () => {
        const urlParams = new URLSearchParams(window.location.search);

        // ✅ Handle auth-code flow (Google redirects with ?code=...)
        const code = urlParams.get('code');
        if (code) {
            console.log('✅ Found auth code in URL:', code);

            // Clean up the URL (remove code from address bar)
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);

            // Call login handler with code
            handleGoogleLogin({ code });
            return true;
        }

        // Optional: handle legacy implicit flow (access_token in hash)
        if (window.location.hash && window.location.hash.includes('access_token')) {
            try {
                const hashParams = new URLSearchParams(window.location.hash.substring(1));
                const accessToken = hashParams.get('access_token');

                if (accessToken) {
                    console.log("Found access token in URL hash");

                    // Clean up the URL
                    const cleanUrl = window.location.href.split('#')[0];
                    window.history.replaceState({}, document.title, cleanUrl);

                    handleGoogleLogin({ access_token: accessToken });
                    return true;
                }
            } catch (error) {
                console.error("Error processing implicit flow:", error);
            }
        }

        return false;
    };

    useEffect(() => {
        preloadIcons(); // preload once
    }, []);

    // Spin logo on app load - state will be set to false by onAnimationEnd

    // Effect to show "What's Happening Today" popup on app load
    useEffect(() => {
        const showPopupOnAppLoad = async () => {
            // Only show for authenticated users (not guests)
            if (!chatId || chatId === 'null' || chatId === 'guest') {
                return;
            }

            // Only show on home or zones view
            if (view !== 'home' && view !== 'zones') {
                return;
            }

            // Check if we should show the popup (once per day)
            if (!shouldShowWhatsHappeningPopup('app')) {
                return;
            }

            // Fetch what's happening today
            try {
                const data = await fetchWhatsHappeningInUserZones(chatId, cacheSafeFetch);

                // Only show if there's actually content
                if (data.openBusinesses.length > 0 || data.todayEvents.length > 0) {
                    setWhatsHappeningData(data);
                    setShowWhatsHappeningPopup(true);
                    markWhatsHappeningPopupShown('app');
                }
            } catch (error) {
                console.error('Failed to fetch what\'s happening today:', error);
            }
        };

        // Small delay to let the app render first
        const timer = setTimeout(showPopupOnAppLoad, 1000);
        return () => clearTimeout(timer);
    }, [chatId, view]);

    useEffect(() => {
        const handlePopState = () => {
            const pathname = window.location.pathname;
            const params = new URLSearchParams(window.location.search);
            const viewParam = params.get("view");
            const entityParam = params.get("entity");
            const groupParam = params.get("group_id") || params.get("group");

            console.log('🔍 handlePopState:', { pathname, viewParam, entityParam });

            // Handle store-invite route explicitly
            if (pathname === '/store-invite') {
                console.log('🔍 Setting view to store-invite');
                setView('store-invite');
                return;
            }

            // Handle join-zone route explicitly
            if (pathname === '/join-zone') {
                console.log('🔍 Setting view to join-zone');
                setView('join-zone');
                return;
            }

            // Handle admin route explicitly
            if (pathname === '/admin') {
                console.log('🔍 Setting view to admin');
                setView('admin');
                return;
            }

            // Handle entity parameter - automatically show business view
            if (entityParam && !viewParam) {
                console.log('🔍 Setting view to business from entity param');
                setView('business');
                return;
            }

            // Handle group parameter - automatically show group view
            if (groupParam && !viewParam) {
                console.log('🔍 Setting view to group from group param');
                setView('group');
                return;
            }

            // Handle store-admin view with slug parameter
            if (viewParam === 'store-admin') {
                const slug = params.get('slug') || pathname.substring(1);
                setView('store-admin');
                setStoreSlug(slug);
            }
            // Check if path is a store slug (not empty and not just "/")
            else if (pathname && pathname !== '/' && !viewParam) {
                // Extract slug from path (remove leading slash)
                const slug = pathname.substring(1);
                setView('store');
                setStoreSlug(slug);
            } else {
                const newView = viewParam || (isGuest ? 'zones' : 'home');
                setView(newView);
                // Extract slug from path if present for store view
                if (newView === 'store' && pathname && pathname !== '/') {
                    setStoreSlug(pathname.substring(1));
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        window.setView = setView; // 🔥 global setter in case dispatch fails

        handlePopState(); // initialize

        return () => {
            window.removeEventListener('popstate', handlePopState);
            delete window.setView;
        };
    }, []);


    // useEffect(() => {
    //     const fetchUserNotifications = async () => {
    //         try {
    //             const res = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-notifications?chat_id=${chatId}`, chatId);
    //             const data = await res.json();
    //             const safeData = data || {};

    //             const allNotifications = Array.isArray(safeData)
    //                 ? safeData
    //                 : Array.isArray(safeData.notifications)
    //                     ? safeData.notifications
    //                     : [];

    //             const lastRead = parseInt(safeData.last_read || "0");
    //             const sortedNotifications = [...allNotifications].sort((a, b) => parseInt(b.created) - parseInt(a.created));
    //             const unread = sortedNotifications.filter(n => parseInt(n.created) > lastRead);

    //             setUserNotifications(sortedNotifications);
    //             setUnreadCount(unread.length);
    //         } catch (err) {
    //             console.error('❌ Failed to fetch notifications:', err);
    //         }
    //     };

    //     if (!isGuest && chatId !== null && chatId !== 'null') {
    //         fetchUserNotifications();
    //     }
    // }, [chatId, isGuest]);

    const mainMenuProps = {
        chatId,
        showShareOptions,
        setShowShareOptions,
        hasBusinesses: userBusinesses.length > 0,
        isGuest,
    };

    // Wait for auth check to complete before rendering anything
    if (!authChecked) {
        return null; // or a loading spinner if preferred
    }

    // Only show login prompt if not authenticated AND not viewing a shared link
    if ((chatId === null || chatId === 'null') && !isSharedLink) {
        return (
            <LoginPrompt
                onGoogleLogin={handleGoogleLogin}
                onTelegramLogin={() => window.location.href = 'https://t.me/MekomitBot'}
                onGuestContinue={() => {
                    const redirect = sessionStorage.getItem('redirectAfterLogin');
                    // Always set guest chat id so app exits auth screen
                    localStorage.setItem('chat_id', 'guest');
                    if (redirect) {
                        sessionStorage.removeItem('redirectAfterLogin');
                    }
                    // Chose guest over login — drop any pending login-return target.
                    sessionStorage.removeItem('loginReturnUrl');
                    navigate('zones');
                    window.location.reload(); // force reload to re-init state as guest
                }}
            />
        );
    }

    return (
        <GoogleOAuthProvider clientId="700733605957-2sej2crh17hi5g7ukuma6id6r4thcv1e.apps.googleusercontent.com">
            {/* Scroll to top whenever view changes */}
            <ScrollToTop view={view} />

            {/* Fixed, always-on-top floating logo for all pages (clickable to go home) */}
            <button
                type="button"
                onClick={() => {
                    setIsShimmering(true);
                    setIsSpinning(true);
                    setTimeout(() => setIsShimmering(false), 800);
                    navigate('home');
                }}
                aria-label="חזור לדף הבית"
                className="fixed left-1/2 -translate-x-1/2 -top-[6px] z-[110] select-none m-0 rounded-full"
                style={{ width: 'auto', outline: 'none' }}
                onMouseDown={(e) => e.preventDefault()} // prevent persistent focus outline
            >
                <div className="relative overflow-visible rounded-full">
                    {/* Warm cream glow effect */}
                    <div
                        className="absolute -inset-6 sm:-inset-8 rounded-full pointer-events-none"
                        style={{
                            background: 'radial-gradient(circle, rgba(255,228,181,0.4) 0%, rgba(255,218,160,0.2) 40%, transparent 70%)',
                            filter: 'blur(16px)',
                        }}
                    />

                    {/* Inner glow for depth */}
                    <div
                        className="absolute -inset-4 sm:-inset-6 rounded-full pointer-events-none"
                        style={{
                            background: 'radial-gradient(circle, rgba(255,245,210,0.3) 0%, transparent 60%)',
                            filter: 'blur(8px)',
                        }}
                    />

                    {/* Logo image */}
                    <img
                        src={logo}
                        alt="לוגו המקומית - לחץ לחזרה לדף הבית"
                        className="w-[6.65rem] h-[6.65rem] sm:w-[9.5rem] sm:h-[9.5rem] object-contain cursor-pointer relative z-10"
                        style={isSpinning ? { animation: 'spin 1s linear forwards' } : {}}
                        onAnimationEnd={() => setIsSpinning(false)}
                        loading="lazy"
                        draggable={false}
                    />

                    {/* Shimmer effect overlay */}
                    {isShimmering && (
                        <div
                            className="absolute inset-0 pointer-events-none z-20 rounded-full"
                            style={{
                                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)',
                                animation: 'shimmer 0.8s ease-in-out',
                            }}
                        />
                    )}
                </div>
            </button>
            {/* Guard against undefined/empty background values. If bgImage falsy, remove background-image declaration entirely. */}
            <div
                className="fixed top-0 left-0 w-full h-full bg-center bg-cover -z-10"
                style={bgImage ? { backgroundImage: `url(${bgImage})` } : {}}
            />
            <div className="relative min-h-screen flex flex-col">

                {/* Hide Navbar when in store or store-admin views (they have their own headers) */}
                {view !== 'store' && view !== 'store-admin' && (
                    <Navbar
                        view={view}
                        notifications={userNotifications}
                        chatId={chatId}
                        unreadCount={unreadCount}
                        setUnreadCount={setUnreadCount}
                        activeTab={view === 'zone-details' ? zoneDetailsActiveTab : null}
                        onTabChange={view === 'zone-details' ? setZoneDetailsActiveTab : null}
                    />
                )}

                <div dir="rtl" className="flex flex-col min-h-screen relative overflow-x-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 100 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="flex-1 text-white pb-24 sm:pb-32"
                    >
                        <div className="flex-1">
                            {/* Start page is now HomePage with personalized updates */}
                            {view === 'home' && <HomePageView navigate={navigate} chatId={chatId} safeFetch={safeFetch} />}
                            {view === 'zones' && <UserZones chatId={chatId || 'guest'} />}
                            {view === 'around-me' && <AroundMe chatId={chatId || 'guest'} />}
                            {view === 'contact' && <Contact chatId={chatId || 'guest'} />}
                            {view === 'about' && <About chatId={chatId || 'guest'} onAddNew={() => setShowCreateOptions(true)} />}
                            {view === 'submit' && !isGuest && new URLSearchParams(window.location.search).get('type') === 'zone' ? (
                                <CreateZoneForm chatId={chatId} />
                            ) : view === 'submit' && !isGuest ? (
                                <RegisterBusinessForm chatId={chatId} initialType={new URLSearchParams(window.location.search).get('type')} />
                            ) : null}
                            {view === 'my-businesses' && !isGuest && <UserBusinesses chatId={chatId} />}
                            {view === 'business' && (
                                <Business
                                    key={new URLSearchParams(window.location.search).get("entity")} // 👈 this forces remount
                                    chatId={chatId || 'guest'}
                                    onLoaded={setSelectedBusiness}
                                />
                            )}
                            {view === 'group' && (
                                <GroupPage />
                            )}
                            {view === 'create-post' && (
                                <CreateCommunityPost
                                    chatId={chatId}
                                    postType={new URLSearchParams(window.location.search).get("post_type")}
                                    onSubmitSuccess={async () => {
                                    }}
                                />
                            )}
                            {view === 'zone-details' && <ZoneDetails activeTab={zoneDetailsActiveTab} onTabChange={setZoneDetailsActiveTab} />}
                            {view === 'store' && storeSlug && (
                                <StoreView
                                    key={storeSlug}
                                    storeSlug={storeSlug}
                                    chatId={chatId || 'guest'}
                                />
                            )}
                            {view === 'store-admin' && storeSlug && (
                                <StoreAdmin
                                    key={storeSlug}
                                    storeSlug={storeSlug}
                                    chatId={chatId || 'guest'}
                                />
                            )}
                            {view === 'store-invite' && (
                                isGuest ? (
                                    <GuestAuthPrompt
                                        title="נדרשת התחברות"
                                        description="כדי לקבל את ההזמנה לניהול החנות יש להתחבר עם חשבון Google או Telegram."
                                        primaryLabel="התחבר עכשיו"
                                        onPrimary={() => {
                                            const fullPath = window.location.pathname + window.location.search;
                                            sessionStorage.setItem('redirectAfterLogin', fullPath);
                                            const token = new URLSearchParams(window.location.search).get('token');
                                            if (token) sessionStorage.setItem('pendingInviteToken', token);
                                            navigate('zones');
                                            window.location.reload();
                                        }}
                                        secondaryLabel="חזרה לקהילות"
                                        onSecondary={() => navigate('zones')}
                                        footnote="מצב אורח לא מאפשר קבלת הזמנת ניהול חנות."
                                    />
                                ) : (
                                    <StoreInviteAccept chatId={chatId} />
                                )
                            )}
                            {view === 'join-zone' && (
                                isGuest ? (
                                    <GuestAuthPrompt
                                        title="נדרשת התחברות"
                                        description="כדי להצטרף לקהילה פרטית יש להתחבר עם חשבון Google או Telegram."
                                        primaryLabel="התחבר עכשיו"
                                        onPrimary={() => {
                                            const fullPath = window.location.pathname + window.location.search;
                                            sessionStorage.setItem('redirectAfterLogin', fullPath);
                                            const inviteCode = new URLSearchParams(window.location.search).get('invite');
                                            if (inviteCode) sessionStorage.setItem('pendingZoneInvite', inviteCode);
                                            navigate('zones');
                                            window.location.reload();
                                        }}
                                        secondaryLabel="חזרה לקהילות"
                                        onSecondary={() => navigate('zones')}
                                        footnote="מצב אורח לא מאפשר הצטרפות לקהילות פרטיות."
                                    />
                                ) : (
                                    <JoinZone chatId={chatId} />
                                )
                            )}
                            {view === 'admin' && (
                                <AdminDashboard chatId={chatId || 'guest'} />
                            )}
                            {view === 'favorites' && (
                                <FavoritesView navigate={navigate} chatId={chatId || 'guest'} safeFetch={safeFetch} />
                            )}
                            {view === 'favorites-list' && (
                                <SharedFavoritesList chatId={chatId || 'guest'} />
                            )}
                            {/* Share Options — unified ShareMenu primitive */}
                            <ShareMenu
                                isOpen={showShareOptions}
                                onClose={() => setShowShareOptions(false)}
                                url="https://hamekomit.com"
                                title="בואו לבקר במקומית"
                                heading="שתף את המקומית"
                            />
                        </div>
                    </motion.div>
                </div>

                {/* Hide FooterNav when in store-admin view (StoreAdmin has its own bottom nav) */}
                {view !== 'store-admin' && <FooterNav chatId={chatId || 'guest'} onAddNew={() => setShowCreateOptions(true)} />}
            </div>

            {/* Create Options — unified BottomSheet with consistent picker tiles */}
            <BottomSheet
                isOpen={showCreateOptions}
                onClose={() => setShowCreateOptions(false)}
                title="מה תרצו ליצור?"
                subtitle="בחרו את סוג הפריט שתרצו להוסיף"
            >
                <div className="grid grid-cols-3 gap-3">
                    {[
                        { label: 'עסק',    icon: Store,     onClick: () => { navigate('submit');                setShowCreateOptions(false); } },
                        { label: 'בארטר',  icon: Handshake, onClick: () => { navigate('submit', 'type=barter'); setShowCreateOptions(false); } },
                        { label: 'מפגש',   icon: Calendar,  onClick: () => { navigate('submit', 'type=event');  setShowCreateOptions(false); } },
                    ].map((opt) => {
                        const Icon = opt.icon;
                        return (
                            <button
                                key={opt.label}
                                onClick={opt.onClick}
                                className="group flex flex-col items-center gap-3 p-4 rounded-xl bg-surface-muted border border-line-subtle hover:bg-brand-50 hover:border-brand-200 hover:-translate-y-0.5 transition-[background-color,border-color,transform,box-shadow] duration-200 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                            >
                                <span className="inline-flex items-center justify-center w-14 h-14 rounded-pill bg-surface border border-line-subtle group-hover:border-brand-200 group-hover:bg-white shadow-xs transition-colors">
                                    <Icon className="w-6 h-6 text-brand-700" strokeWidth={1.75} />
                                </span>
                                <span className="text-sm font-semibold text-ink">{opt.label}</span>
                            </button>
                        );
                    })}
                </div>
            </BottomSheet>

            {/* Post Type Options — unified BottomSheet picker */}
            <BottomSheet
                isOpen={showPostOptions}
                onClose={() => setShowPostOptions(false)}
                title="בחרו סוג פוסט"
            >
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {[
                        { label: 'סקר',              image: icons.poll,                type: 'poll' },
                        { label: 'המלצות',           image: icons.recommendations,     type: 'recommendations' },
                        { label: 'קח-תן / מסירות',   image: icons.givetake,            type: 'givetake' },
                        { label: 'בתים',             image: icons.houses,              type: 'houses' },
                        { label: 'אחר',              image: icons.other,               type: 'other' },
                        { label: 'יוזמה קהילתית',     image: icons.communityInitiative, type: 'communityInitiative' },
                    ].map((opt) => (
                        <button
                            key={opt.type}
                            onClick={() => { setShowPostOptions(false); navigate('create-post', `post_type=${opt.type}`); }}
                            className="flex flex-col items-center gap-2 p-4 rounded-xl bg-surface-muted border border-line-subtle hover:bg-brand-50 hover:border-brand-200 hover:-translate-y-0.5 transition-[background-color,border-color,transform,box-shadow] duration-200 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        >
                            <img src={opt.image} alt="" aria-hidden="true" className="w-14 h-14 object-contain" />
                            <span className="text-sm font-semibold text-ink text-center">{opt.label}</span>
                        </button>
                    ))}
                </div>
                <div className="mt-5 flex justify-center">
                    <Button variant="ghost" onClick={() => { setShowPostOptions(false); setShowCreateOptions(true); }}>
                        חזור
                    </Button>
                </div>
            </BottomSheet>

            {/* AI Chat Bar - visible on all pages when logged in */}
            {chatId && chatId !== 'null' && <AIChatBar />}

            {/* Poll the user's stores for new orders while the tab is open */}
            <NewOrdersNotifier chatId={chatId} />

            {/* What's Happening Today Popup */}
            <WhatsHappeningTodayPopup
                isOpen={showWhatsHappeningPopup}
                onClose={() => setShowWhatsHappeningPopup(false)}
                openBusinesses={whatsHappeningData.openBusinesses}
                todayEvents={whatsHappeningData.todayEvents}
            />
        </GoogleOAuthProvider>
    );
}